import { useEffect, useRef, useState } from 'react';
import DataGrid, {
    Column,
    Editing,
    Lookup,
    Toolbar,
    Item
} from 'devextreme-react/data-grid';
import InfoHeader from '../components/InfoHeader';
const TcmCostEstimateDetails = ({
    estimate,
    onChange,
    editing,
    priceBooks
}) => {
    const detailGridRef = useRef(null);
    const [details, setDetails] = useState(estimate.TCMCostEstimateDetails || []);
    const tempIdRef = useRef(-1);

    // Keep internal details in sync when a different estimate is being edited
    useEffect(() => {
        setDetails(estimate.TCMCostEstimateDetails || []);
    }, [estimate]);

    // Push updated details back up to the parent estimate whenever they change
    useEffect(() => {
        onChange({
            ...estimate,
            TCMCostEstimateDetails: details
        });
    }, [details]);

    const handleRowInserting = (e) => {
        // Cancel default DevExtreme handling and manage via React state
        e.cancel = true;

        const newDetail = {
            Id: tempIdRef.current--,
            StatusId: e.data.StatusId ?? 0,
            ItemDescription: e.data.ItemDescription || '',
            CostEstimateId: estimate.Id || 0,
            PriceBookId: e.data.PriceBookId ?? 0,
            UnitPrice: e.data.UnitPrice ?? 0,
            Quantity: e.data.Quantity ?? 0,
            LaborPrice: e.data.LaborPrice ?? 0,
            LaborHours: e.data.LaborHours ?? 0,
            Comments: e.data.Comments || '',
            // CreatedBy: null,
            // CreatedDate: null,
            // UpdatedBy: null,
            // UpdatedDate: null
        };

        setDetails((prev) => [...prev, newDetail]);
        // console.log("e after insert:", e);
        e.component.cancelEditData();
    };

    const handleRowUpdating = (e) => {
        e.cancel = true;

        setDetails((prev) =>
            prev.map((d) =>
                d.Id === e.key
                    ? {
                        ...d,
                        ...e.newData
                    }
                    : d
            )
        );
        e.component.cancelEditData();
    };

    return (
        <DataGrid
            id='tcmCostEstimateDetailsDataGrid'
            dataSource={details}
            showBorders={true}
            columnAutoWidth={true}
            ref={detailGridRef}
            keyExpr="Id"
            onRowUpdating={handleRowUpdating}
            onRowInserting={handleRowInserting}
        >
            <Toolbar visible={editing} >
                <Item name="addRowButton" showText="always" caption="Add Line Item" />
            </Toolbar>
            <Editing
                mode="row"
                allowUpdating={editing}
                allowAdding={editing}
            />
            <Column
                dataField="ItemDescription"
                caption="Item Description"
                dataType="string"
                validationRules={[
                    {
                        type: 'required',
                        message: 'Item Description is required.'
                    }
                ]}
            />
            <Column
                dataField="UnitPrice"
                caption="Unit Price"
                dataType="number"
                format={{ type: 'currency', precision: 2 }}
            />
            <Column
                dataField="PriceBookId"
                caption="Unit of Issue"
                validationRules={[
                    {
                        type: 'required',
                        message: 'Unit of Issue is required.'
                    }
                ]}
            >
                <Lookup
                    dataSource={priceBooks}
                    displayExpr="UnitOfIssue"
                    valueExpr="Id"
                />
            </Column>
            <Column
                dataField="Quantity"
                caption="Quantity"
                dataType="number"
                validationRules={[
                    {
                        type: 'required',
                        message: 'Quantity is required.'
                    },
                    {
                        type: 'custom',
                        message: 'Quantity must be greater than 0.',
                        validationCallback: (e) => {
                            const value = Number(e.value);
                            return !Number.isNaN(value) && value > 0;
                        }
                    }
                ]}
            />

            <Column
                dataField="LaborPrice"
                caption="Labor Rate"
                dataType="number"
                format={{ type: 'currency', precision: 2 }}
            />
            <Column dataField="LaborHours" caption="Labor Hours" dataType="number" />
            <Column dataField="Comments" caption="Comments" />
            <Column
                caption="Unit Cost"
                dataType="number"
                format={{ type: 'currency', precision: 2 }}
                allowEditing={false}
                headerCellRender={() =>
                    <InfoHeader
                        label="Unit Cost"
                        tooltip="Unit Price * Quantity"
                    />
                }
                calculateCellValue={rowData => {
                    // Ensure UnitPrice and Quantity are numbers, default to 0 if invalid
                    const unitPrice = Number(rowData.UnitPrice);
                    const quantity = Number(rowData.Quantity);

                    // If either is NaN, use 0
                    const safeUnitPrice = isNaN(unitPrice) ? 0 : unitPrice;
                    const safeQuantity = isNaN(quantity) ? 0 : quantity;

                    const value = safeUnitPrice * safeQuantity;
                    return Number(value.toFixed(2));
                }}
            />
            <Column
                dataField="LaborCost"
                caption="Labor Cost"
                dataType="number"
                format={{ type: 'currency', precision: 2 }}
                allowEditing={false}
                headerCellRender={() =>
                    <InfoHeader
                        label="Labor Cost"
                        tooltip="Labor Rate * Labor Hours"
                    />
                }
                calculateCellValue={rowData => {
                    // Ensure LaborPrice and LaborHours are numbers, default to 0 if invalid
                    const laborPrice = Number(rowData.LaborPrice);
                    const laborHours = Number(rowData.LaborHours);

                    // If either is NaN, use 0
                    const safeLaborPrice = isNaN(laborPrice) ? 0 : laborPrice;
                    const safeLaborHours = isNaN(laborHours) ? 0 : laborHours;

                    const value = safeLaborPrice * safeLaborHours;
                    return Number(value.toFixed(2));
                }}
            />
            <Column
                dataField="GrandTotal"
                caption="Total Cost"
                dataType="number"
                format={{ type: 'currency', precision: 2 }}
                allowEditing={false}
                headerCellRender={() =>
                    <InfoHeader
                        label="Total Cost"
                        tooltip="Labor Cost + Unit Cost"
                    />
                }
                calculateCellValue={rowData => {
                    // Ensure are numbers, default to 0 if invalid
                    const laborPrice = Number(rowData.LaborPrice);
                    const laborHours = Number(rowData.LaborHours);
                    const unitPrice = Number(rowData.UnitPrice);
                    const quantity = Number(rowData.Quantity);
                    // If either is NaN, use 0
                    const safeLaborPrice = isNaN(laborPrice) ? 0 : laborPrice;
                    const safeLaborHours = isNaN(laborHours) ? 0 : laborHours;
                    const safeUnitPrice = isNaN(unitPrice) ? 0 : unitPrice;
                    const safeQuantity = isNaN(quantity) ? 0 : quantity;
                    const value = ((safeUnitPrice * safeQuantity) + (safeLaborPrice * safeLaborHours));
                    return Number(value.toFixed(2));
                }

                }
            />

        </DataGrid>
    )

}
export default TcmCostEstimateDetails;